import React from 'react';
import * as Sentry from '@sentry/react';
import { Button, Typography } from '@mui/material';
import ClaveUnicaDialog from '../ClaveUnicaDialog/redesign';
import ClaveUnicaErrorDialog from '../ClaveUnicaDialog/errorDialogRedesign';
import analytics from '../../utils/analytics';
import { Partner } from '../LeadFormIndex/types';
import jwtDecode from '../../utils/jwtDecode';

interface ClaveUnicaFormInterface<T>{
  idNumber: string,
  origin: string,
  token: string,
  pageCall: {category: string, name: string},
  callback: (payload: T) => void,
  endpoint: (params: ClaveUnicaEndpointProps) => Promise<T>,
  firstMessage?: React.ReactElement|null,
  secondMessage?: React.ReactElement|null,
  showDisclaimer?: boolean,
  button?: React.ReactElement|null,
  subtitle?: string,
  partner: Partner,
  customLogo?: string,
  customLoadingLogo?: string,
  fallback?: (token: string)=> Promise<T>,
  fallbackUnemployed?: () => Promise<void>,
  fallbackManualEvaluation?: () => Promise<void>,
  variant?: 'dialog' | 'inline',
  onChangeRut?: () => void,
  submitLabel?: string,
  onLoadingChange?: (loading: boolean) => void,
}

export type ClaveUnicaEndpointProps = {
  token: string,
  password: string,
  code?: string,
}

export default function ClaveUnicaFormRedesign<T>(props: ClaveUnicaFormInterface<T>) {
  const {
    token,
    endpoint,
    idNumber,
    origin,
    callback,
    firstMessage,
    secondMessage,
    showDisclaimer,
    button,
    subtitle,
    partner,
    pageCall,
    customLogo,
    customLoadingLogo,
    fallback,
    fallbackUnemployed,
    fallbackManualEvaluation,
    variant = 'dialog',
    onChangeRut,
    submitLabel,
    onLoadingChange,
  } = props;

  const [isPasswordError, setIsPasswordError] = React.useState(false);
  const [isBlockedPasswordError, setIsBlockedPasswordError] = React.useState(false);
  const [isDataExtractionError, setIsDataExtractionError] = React.useState(false);
  const [isMaintenanceError, setIsMaintenanceError] = React.useState(false);
  const [isAFCBlockedPasswordError, setIsAFCBlockedPasswordError] = React.useState(false);
  const [isNotSubscribedError, setIsNotSubscribedError] = React.useState(false);
  const [isConnectionError, setIsConnectionError] = React.useState(false);
  const [isExternalInformationError, setIsExternalInformationError] = React.useState(false);
  const [passwordNumTries, setPasswordNumTries] = React.useState(0);
  const [needAuthFactor, setNeedAuthFactor] = React.useState(false);
  const [facialAuthFactor, setFacialAuthFactor] = React.useState(false);
  const [urlFacialAuthFactor, setUrlFacialAuthFactor] = React.useState('');
  const [triggerSubmit, setTriggerSubmit] = React.useState(false);
  const [email, setEmail] = React.useState('');

  const parseErrorStatus = (error: unknown): number | undefined => {
    if (typeof error === 'number') {
      return error;
    }
    if (typeof error === 'object' && error !== null && 'status' in error) {
      const { status } = error as { status?: unknown };
      return typeof status === 'number' ? status : undefined;
    }
    if (error === 'ERR_CLAVE_UNICA_BLOCKED_PASSWORD') {
      return 422;
    }
    return undefined;
  };

  const retryCUSubmit = () => {
    setTriggerSubmit(true);
    setTimeout(() => setTriggerSubmit(false), 1500);
  };

  const fallbackAction = async () => {
    try {
      if (!fallback) {
        return 400;
      }
      const res = await fallback(token);
      callback(res);
      return 200;
    } catch (e) {
      return e as number;
    }
  };

  const getClaveUnicaData = async (password: string, code?: string): Promise<number> => {
    setIsPasswordError(false);
    setIsBlockedPasswordError(false);
    setIsDataExtractionError(false);
    let res;
    try {
      res = await endpoint({ token, password, code });
      if ((res as any)?.status === 207) {
        setNeedAuthFactor(true);
        setEmail((res as any)?.data?.email);
        return 207;
      }
      if ((res as any)?.status === 208) {
        setFacialAuthFactor(true);
        setUrlFacialAuthFactor((res as any)?.data?.url);
        return 208;
      }
    } catch (e) {
      const errorStatus = parseErrorStatus(e);
      const category = `${pageCall.category.split('-')[0].trim()} - ERROR CU`;
      if (facialAuthFactor && fallback) {
        analytics.page(category, 'ERROR DE VERIFICACIÓN FACIAL');
        const r = await fallbackAction();
        return r;
      }
      if (errorStatus === 402) {
        setPasswordNumTries((prevState) => prevState + 1);
        analytics.page(category, 'CU INCORRECTA');
        setIsPasswordError(true);
        return 402;
      }
      if (errorStatus === 422) {
        analytics.page(category, 'CU BLOQUEADA');
        setIsBlockedPasswordError(true);
        return 422;
      }
      if (errorStatus === 423) {
        analytics.page(category, 'CONTRASEÑA AFC BLOQUEADA');
        setIsAFCBlockedPasswordError(true);
        return 423;
      }
      if (errorStatus === 504) {
        analytics.page(category, 'SERVICIO CONSULTADO EN MANTENCION');
        setIsMaintenanceError(true);
        return 504;
      }
      if (errorStatus === 505) {
        analytics.page(category, 'ERROR DE CONEXION');
        setIsConnectionError(true);
        return 505;
      }
      if (errorStatus === 506) {
        analytics.page(category, 'USUARIO NO INSCRITO EN SERVICIO');
        setIsNotSubscribedError(true);
        return 506;
      }
      if (errorStatus === 509) {
        analytics.page(category, 'ERROR DE FUENTE INFORMACION EXTERNA');
        setIsExternalInformationError(true);
        return 509;
      }
      setPasswordNumTries((prevState) => prevState + 1);
      analytics.page(category, 'ERROR DE EXTRACIÓN DE DATOS', { errorCode: e });
      const { preLoanRequestId } = jwtDecode(token);
      Sentry.setUser({ id: idNumber });
      Sentry.setExtra('idNumber', idNumber);
      Sentry.setExtra('preLoanRequestId', preLoanRequestId);
      Sentry.setExtra('origin', origin);
      Sentry.setExtra('error', e);
      Sentry.captureMessage('DATA EXTRACTION ERROR', 'fatal');
      setIsDataExtractionError(true);
      return 400;
    }
    callback(res);
    return 200;
  };

  return (
    <>
      { firstMessage }
      { secondMessage }
      {/* Inline (2g): columna flexible para que el consentimiento quede pegado al footer. */}
      <div style={variant === 'inline' ? { flex: 1, display: 'flex', flexDirection: 'column' } : undefined}>
        {showDisclaimer
        && (
        <Typography
          align="center"
          gutterBottom
          fontStyle="italic"
          style={{ color: partner.config.styles?.secondaryColor, marginTop: '20px' }}
          variant="body2"
        >
          no se guardarán tus credenciales
        </Typography>
        )}
        <ClaveUnicaDialog
          rut={idNumber}
          action={(password, code) => getClaveUnicaData(password, code)}
          maximumTries={passwordNumTries > 2
              || isAFCBlockedPasswordError
              || isNotSubscribedError
              || isMaintenanceError
              || isExternalInformationError}
          triggerSubmit={triggerSubmit}
          button={button as React.ReactElement|null}
          subtitle={subtitle as string}
          origin={origin}
          pageCall={pageCall}
          customLogo={customLogo}
          customLoadingLogo={customLoadingLogo}
          partner={partner}
          needAuthFactor={needAuthFactor}
          email={email}
          facialAuthFactor={facialAuthFactor}
          urlFacialAuthFactor={urlFacialAuthFactor}
          fallback={fallbackAction}
          fallbackUnemployed={fallbackUnemployed}
          fallbackManualEvaluation={fallbackManualEvaluation}
          variant={variant}
          onChangeRut={onChangeRut}
          submitLabel={submitLabel}
          onLoadingChange={onLoadingChange}
        />
      </div>
      <ClaveUnicaErrorDialog
        errors={{
          isPasswordError,
          isDataExtractionError,
          isBlockedPasswordError,
          isMaintenanceError,
          isAFCBlockedPasswordError,
          isNotSubscribedError,
          isConnectionError,
          isExternalInformationError,
        }}
        numTries={passwordNumTries}
        partner={partner}
        retryCUSubmit={retryCUSubmit}
      />
    </>
  );
}

ClaveUnicaFormRedesign.defaultProps = {
  firstMessage: null,
  secondMessage: null,
  showDisclaimer: true,
  button:
  <Button
    data-pw="cu-open-dialog-button"
    variant="contained"
  >
    Ingresar ClaveÚnica
  </Button>,
  subtitle: 'Necesitamos que ingreses tu Clave Única para verificar tus datos.',
  customLogo: undefined,
  customLoadingLogo: undefined,
  fallback: undefined,
  fallbackUnemployed: undefined,
  fallbackManualEvaluation: undefined,
  variant: 'dialog',
  onChangeRut: undefined,
  submitLabel: undefined,
  onLoadingChange: undefined,
};
